import React from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle, Upload, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_FIELDS = [
  { key: "criminal_record", label: "Straffeattest", required: true, hint: "PDF eller foto af din straffeattest (kun straffeattest til arbejde, ikke privat)" },
  { key: "drivers_license_front", label: "Kørekort — forside", required: true, hint: "Tydeligt foto af kørekortets forside" },
  { key: "drivers_license_back", label: "Kørekort — bagside", required: true, hint: "Tydeligt foto af kørekortets bagside" },
  { key: "residence_permit", label: "Opholdstilladelse", required: false, hint: "Hvis du har en — ellers spring over" },
  { key: "passport_photo", label: "Pasbillede / portræt", required: true, hint: "Klart portrætfoto til id-kort" },
];

export default function OnboardingPage() {
  const { token } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [tokenInfo, setTokenInfo] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [form, setForm] = React.useState({
    first_name: "",
    last_name: "",
    cpr: "",
    address: "",
    zip: "",
    city: "",
    phone: "",
    email: "",
    license_plate: "",
    bank_reg: "",
    bank_account: "",
    gdpr_consent: false,
  });
  const [files, setFiles] = React.useState({});

  React.useEffect(() => {
    document.title = "Ansættelses-onboarding · EMA Kurer ApS";
    (async () => {
      try {
        const { data } = await axios.get(`${API}/onboarding/${token}/info`);
        setTokenInfo(data);
        const parts = (data.full_name || "").split(" ");
        setForm((f) => ({
          ...f,
          first_name: parts[0] || "",
          last_name: parts.slice(1).join(" ") || "",
          email: data.email || "",
        }));
        if (data.used) {
          setError("Dette onboarding-link er allerede brugt. Kontakt EMA Kurer hvis du har brug for et nyt.");
        }
      } catch (e) {
        const msg = e.response?.data?.detail || "Ugyldigt eller udløbet link";
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (k) => (e) => {
    const f = e.target.files?.[0];
    if (f) setFiles((s) => ({ ...s, [k]: f }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) { toast.error("Du skal acceptere GDPR-samtykke"); return; }
    const requiredFiles = DOC_FIELDS.filter((d) => d.required).map((d) => d.key);
    for (const k of requiredFiles) {
      if (!files[k]) { toast.error(`Manglende dokument: ${DOC_FIELDS.find(d=>d.key===k).label}`); return; }
    }
    const cpr = form.cpr.replace(/\D/g, "");
    if (cpr.length !== 10) { toast.error("CPR-nummer skal være 10 cifre"); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, k === "gdpr_consent" ? String(v) : v));
      Object.entries(files).forEach(([k, f]) => fd.append(k, f));
      const { data } = await axios.post(`${API}/onboarding/${token}/submit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.ok) {
        setSubmitted(true);
        toast.success("Tak! Dine oplysninger er sendt til EMA Kurer.");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Der skete en fejl. Prøv igen.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (error) return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center px-5">
      <div className="bg-white border border-brand p-8 max-w-md text-center">
        <AlertTriangle className="mx-auto text-brand-red" size={32} />
        <h1 className="font-display text-2xl text-brand-dark mt-4">Linket virker ikke</h1>
        <p className="text-brand-text2 mt-2">{error}</p>
        <a href="mailto:job@emakurer.dk" className="text-brand-red font-semibold mt-4 inline-block">job@emakurer.dk</a>
      </div>
    </div>
  );
  if (submitted) return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center px-5">
      <div className="bg-white border border-brand p-10 max-w-md text-center" data-testid="onboarding-success">
        <CheckCircle2 className="mx-auto text-emerald-600" size={48} />
        <h1 className="font-display text-3xl text-brand-dark mt-5">Tak — vi har modtaget dine oplysninger! 🎉</h1>
        <p className="text-brand-text2 mt-3 leading-relaxed">
          EMA Kurer gennemgår dine dokumenter de næste 1-2 hverdage. Når alt er godkendt
          sender vi din <strong>ansættelseskontrakt</strong> samt en <strong>samarbejdsaftale med Burd Delivery</strong> til din email.
        </p>
        <p className="text-brand-text2 mt-4 text-sm">Spørgsmål? <a href="mailto:job@emakurer.dk" className="text-brand-red font-semibold">job@emakurer.dk</a></p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="onboarding-page">
      {/* HEADER */}
      <div className="bg-brand-dark text-white py-10 px-5">
        <div className="max-w-3xl mx-auto">
          <div className="kicker text-brand-red">EMA Kurer ApS · Onboarding</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl mt-2">Velkommen, {(form.first_name || tokenInfo?.full_name || "kollega").split(" ")[0]} 👋</h1>
          <p className="text-white/70 mt-3 max-w-2xl">Sidste skridt før din ansættelse: Udfyld dine oplysninger og upload de 5 dokumenter nedenfor. Det tager ca. 5-10 minutter.</p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="max-w-3xl mx-auto px-5 py-10 space-y-8" data-testid="onboarding-form">
        {/* Personlige oplysninger */}
        <section className="bg-white border border-brand p-6">
          <h2 className="font-display text-xl text-brand-dark mb-4">Personlige oplysninger</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-flat">Fornavn *</label>
              <input className="input-flat" value={form.first_name} onChange={upd("first_name")} required data-testid="onb-first-name" />
            </div>
            <div>
              <label className="label-flat">Efternavn *</label>
              <input className="input-flat" value={form.last_name} onChange={upd("last_name")} required data-testid="onb-last-name" />
            </div>
            <div>
              <label className="label-flat flex items-center gap-1"><Lock size={11} /> CPR-nummer *</label>
              <input className="input-flat font-mono" value={form.cpr} onChange={upd("cpr")} placeholder="DDMMÅÅ-XXXX" required data-testid="onb-cpr" maxLength={11} />
              <div className="text-xs text-brand-text2 mt-1">10 cifre · gemmes krypteret · vises kun for HR</div>
            </div>
            <div>
              <label className="label-flat">Mobil *</label>
              <input className="input-flat" type="tel" value={form.phone} onChange={upd("phone")} placeholder="+45 12 34 56 78" required data-testid="onb-phone" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-flat">Adresse *</label>
              <input className="input-flat" value={form.address} onChange={upd("address")} placeholder="Gadenavn + nr." required data-testid="onb-address" />
            </div>
            <div>
              <label className="label-flat">Postnummer *</label>
              <input className="input-flat" value={form.zip} onChange={upd("zip")} placeholder="2300" required data-testid="onb-zip" maxLength={4} />
            </div>
            <div>
              <label className="label-flat">By *</label>
              <input className="input-flat" value={form.city} onChange={upd("city")} placeholder="København S" required data-testid="onb-city" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-flat">Email *</label>
              <input className="input-flat" type="email" value={form.email} onChange={upd("email")} required data-testid="onb-email" />
            </div>
          </div>
        </section>

        {/* Køretøj */}
        <section className="bg-white border border-brand p-6">
          <h2 className="font-display text-xl text-brand-dark mb-4">Køretøj</h2>
          <div>
            <label className="label-flat">Nummerplade på bilen *</label>
            <input className="input-flat font-mono uppercase" value={form.license_plate} onChange={upd("license_plate")} placeholder="AB 12 345" required data-testid="onb-plate" />
          </div>
        </section>

        {/* Bank */}
        <section className="bg-white border border-brand p-6">
          <h2 className="font-display text-xl text-brand-dark mb-4 flex items-center gap-2">
            <Lock size={16} className="text-brand-red" /> Bankoplysninger (lønudbetaling)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="label-flat">Reg.nr *</label>
              <input className="input-flat font-mono" value={form.bank_reg} onChange={upd("bank_reg")} placeholder="1234" required maxLength={4} data-testid="onb-reg" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-flat">Kontonummer *</label>
              <input className="input-flat font-mono" value={form.bank_account} onChange={upd("bank_account")} placeholder="12345678 (eller op til 10 cifre)" required data-testid="onb-account" />
            </div>
          </div>
        </section>

        {/* Dokumenter */}
        <section className="bg-white border border-brand p-6">
          <h2 className="font-display text-xl text-brand-dark mb-4">Dokumenter & billeder</h2>
          <p className="text-sm text-brand-text2 mb-5">PDF, JPG eller PNG. Max 10 MB per fil. Filerne opbevares krypteret.</p>
          <div className="space-y-4">
            {DOC_FIELDS.map((d) => (
              <div key={d.key} className="border border-brand p-4 hover:border-brand-dark transition">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-semibold text-brand-dark">{d.label} {d.required && <span className="text-brand-red">*</span>}</div>
                    <div className="text-xs text-brand-text2">{d.hint}</div>
                  </div>
                  {files[d.key] && <CheckCircle2 size={18} className="text-emerald-600" />}
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Upload size={14} className="text-brand-red" />
                  <span className="text-brand-text2">{files[d.key]?.name || "Vælg fil..."}</span>
                  <input type="file" accept="application/pdf,image/*" onChange={onFile(d.key)} className="hidden" data-testid={`onb-file-${d.key}`} />
                </label>
              </div>
            ))}
          </div>
        </section>

        {/* GDPR */}
        <section className="bg-brand-dark text-white p-6">
          <h2 className="font-display text-xl mb-3">GDPR-samtykke</h2>
          <p className="text-sm text-white/80 leading-relaxed">
            Jeg accepterer at EMA Kurer ApS opbevarer og behandler mine persondata
            (navn, adresse, CPR, kontaktinfo, kørekort, straffeattest, opholdstilladelse,
            kontooplysninger og foto) i forbindelse med min ansættelse. Data slettes 5 år
            efter ansættelsens ophør i henhold til bogføringsloven og GDPR Art. 6 stk. 1 lit. b og f.
          </p>
          <label className="flex items-start gap-3 mt-4 cursor-pointer">
            <input type="checkbox" checked={form.gdpr_consent} onChange={(e) => setForm((f) => ({ ...f, gdpr_consent: e.target.checked }))} className="mt-1" data-testid="onb-gdpr" required />
            <span className="text-sm font-semibold">Jeg accepterer behandling af mine persondata som beskrevet ovenfor</span>
          </label>
        </section>

        <button type="submit" disabled={submitting || !form.gdpr_consent} className="btn-primary w-full justify-center text-base py-4" data-testid="onb-submit">
          {submitting ? <><Loader2 className="animate-spin" size={16} /> Sender...</> : "Send mine oplysninger →"}
        </button>
        <p className="text-xs text-brand-text2 text-center">Linket udløber 14 dage efter du har modtaget det.</p>
      </form>
    </div>
  );
}
